import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import { FaSignOutAlt } from 'react-icons/fa';
import useAuth from "../../Auth/useAuth";
import './Logout.css';
import AlertModal from "../AlertModal/AlertModal";
import config from '../../config';

axios.defaults.baseURL = config.BASE_URL;

const Logout = ()=>{
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    
    // Fullscreen effect states
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [progress, setProgress] = useState(0);
    
    const { setIsAuthenticated,setUsername,setUserProfile,setTripIDState,setModuleChange,setActiveModule,setSearchValue} = useAuth();
    const navigate = useNavigate();

    // Add modal state
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });

    // Modal handler functions
    const showModal = (title, message, type = 'info') => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            type
        });
    };

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    // Handle body scroll and sidebar visibility when modals are open
    useEffect(() => {
        const sidebar = document.querySelector('.sidebar');
        const body = document.body;
        
        if (showConfirm || isProcessing) {
            // Disable body scroll
            body.classList.add('modal-open');
            
            // Hide sidebar when modal or processing overlay is open
            if (sidebar) {
                sidebar.style.zIndex = '1';
                sidebar.style.pointerEvents = 'none';
                sidebar.style.opacity = '0.3';
                sidebar.style.transition = 'opacity 0.3s ease';
            }
        } else {
            // Re-enable body scroll
            body.classList.remove('modal-open');
            
            // Restore sidebar
            if (sidebar) {
                sidebar.style.zIndex = '';
                sidebar.style.pointerEvents = '';
                sidebar.style.opacity = '';
                sidebar.style.transition = '';
            }
        }
        
        // Cleanup function
        return () => {
            body.classList.remove('modal-open');
            if (sidebar) {
                sidebar.style.zIndex = '';
                sidebar.style.pointerEvents = '';
                sidebar.style.opacity = '';
                sidebar.style.transition = '';
            }
        };
    }, [showConfirm, isProcessing]);

    const handleLogout = async () => {
        try {
            setIsLoading(true);
            setShowConfirm(false);
            
            // Small delay before showing processing overlay
            setTimeout(() => {
                setIsProcessing(true);
                setStatusMessage('Logging you out...');
                setProgress(30);
            }, 200);

            const response = await axios.post('/logout', {},
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                    }
                }
            );
            
            if (response.status === 200) {
                // Progress: Logging out
                setTimeout(() => {
                    setProgress(60);
                    setStatusMessage('Clearing session data...');

                    // Progress: Clearing data
                    setTimeout(() => {
                        setProgress(85);
                        setStatusMessage('Logout successful!');

                        // Final progress and redirect
                        setTimeout(() => {
                            setProgress(100);
                            setStatusMessage('Redirecting to login...');

                            setTimeout(() => {
                                setIsAuthenticated(false);
                                setTripIDState('');
                                setActiveModule('upload');
                                sessionStorage.clear();
                                navigate('/');
                            }, 800);
                        }, 600);
                    }, 600);
                }, 800);
            }
        } catch (error) {
            setIsProcessing(false);
            setStatusMessage('');
            setProgress(0);
            setShowConfirm(false);
            
            if (error.response) {
                showModal('Logout Failed', error.response.data.message, 'error');
            } else {
                showModal('Logout Failed', 'An unexpected error occurred. Please try again.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isProcessing && (
                <div className='fullscreen-overlay'>
                    <div className='progress-container'>
                        <div className='logout-animation-icon'>
                            <FaSignOutAlt />
                        </div>
                        <div className='status-message-fullscreen'>
                            {statusMessage}
                        </div>
                        <div className='progress-bar-container'>
                            <div 
                                className='progress-bar-fill' 
                                style={{width: `${progress}%`}}
                            ></div>
                        </div>
                        <div className='progress-percentage'>
                            {progress}%
                        </div>
                    </div>
                </div>
            )}

            <div className='logout-wrapper'>
                <button
                    className={`logout-btn ${isLoading ? 'loading' : ''}`}
                    onClick={() => setShowConfirm(true)}
                    disabled={isLoading}
                >
                    <FaSignOutAlt className='logout-icon'/>
                    <span>Logout</span>
                </button>

                {!isLoading && !modalConfig.isOpen && <AlertModal
                    isOpen={modalConfig.isOpen}
                    onClose={closeModal}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    type={modalConfig.type}
                />}

                {showConfirm && (
                    <div className="logout-confirm-overlay">
                        <div className="logout-confirm-modal">
                            <div className="logout-icon-large">
                                <FaSignOutAlt />
                            </div>
                            <h3>Confirm Logout</h3>
                            <p>Are you sure you want to logout from your account?</p>
                            <div className="logout-confirm-buttons">
                                <button 
                                    className="confirm-btn confirm-yes"
                                    onClick={handleLogout}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Logging out...' : 'Yes, Logout'}
                                </button>
                                <button 
                                    className="confirm-btn confirm-no"
                                    onClick={() => setShowConfirm(false)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Logout;